import { Box, Card, CardContent, keyframes, Stack, TextField, Typography } from '@mui/material';
import { Menu, Star, AccountCircle, AccountTree, LinkSharp } from '@mui/icons-material';
import React, { ReactNode } from 'react';

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.5); }
  100% { transform: scale(1.3); }
`;

interface ShopfloorAppPreviewProps {
  /** Module name shown in the header (e.g., "Finder", "Linker") */
  moduleName: string;
  /** Entity name shown in the content area */
  entityName?: string;
  /** Coordinate shown below the entity name */
  coordinate?: string;
  /** Main content to display in the center */
  children?: ReactNode;
  /** Overlay content (e.g., dialogs) rendered on top of the main content */
  overlay?: ReactNode;
}

export const ShopfloorAppPreview = ({
  moduleName,
  entityName,
  coordinate,
  children,
  overlay,
}: ShopfloorAppPreviewProps): React.JSX.Element => (
  <Box
    sx={{
      border: 3,
      borderColor: '#1a1a1a',
      borderRadius: 4,
      overflow: 'hidden',
      width: 180,
      bgcolor: '#434343',
      boxShadow: 3,
      aspectRatio: '9/16',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
    }}
  >
    {/* App header bar */}
    <Stack
      direction="row"
      alignItems="center"
      sx={{ bgcolor: '#434343', color: '#fff', px: 1, py: 0.5, minHeight: 32 }}
    >
      <Menu sx={{ fontSize: 14, color: '#5fd362' }} />
      <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#fff', ml: 0.5 }}>ekko</Typography>
      <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#5fd362', ml: 0.5 }}>{moduleName}</Typography>
      <Box sx={{ flexGrow: 1 }} />
      <Star sx={{ fontSize: 12, color: '#5fd362', mr: 0.5 }} />
      <AccountCircle sx={{ fontSize: 12, color: '#5fd362' }} />
    </Stack>

    {/* App content area */}
    <Stack spacing={1} sx={{ px: 1.5, py: 1, bgcolor: 'background.default', flexGrow: 1 }}>
      {/* Entity name & coordinate - right aligned */}
      {(entityName || coordinate) && (
        <>
          <Box sx={{ textAlign: 'right' }}>
            {entityName && (
              <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#5fd362' }}>{entityName}</Typography>
            )}
            {coordinate && <Typography sx={{ fontSize: 8, color: 'text.secondary' }}>{coordinate}</Typography>}
          </Box>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }} />
        </>
      )}

      {/* Main content */}
      <Stack spacing={1.5} alignItems="center" justifyContent="center" sx={{ flexGrow: 1 }}>
        {children}
      </Stack>
    </Stack>

    {/* Bottom navigation bar */}
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ bgcolor: '#434343', color: '#fff', px: 1.5, py: 0.75 }}
    >
      <Typography sx={{ fontSize: 8, opacity: 0.7 }}>RESET</Typography>
      <Typography sx={{ fontSize: 8, opacity: 0.7 }}>NEXT</Typography>
    </Stack>

    {/* Overlay content (dialogs, etc.) */}
    {overlay}
  </Box>
);

/** Pre-styled content components for use inside ShopfloorAppPreview */

interface PromptTextProps {
  text?: string;
  defaultText?: string;
  /** Whether the corresponding input field is focused */
  focused?: boolean;
}

export const ShopfloorPromptText = ({
  text,
  defaultText = '',
  focused = false,
}: PromptTextProps): React.JSX.Element => (
  <Box sx={{ width: '75%' }}>
    <Typography
      textAlign="center"
      sx={{
        fontSize: text ? 12 : 10,
        fontWeight: focused ? 700 : 400,
        color: text ? 'text.primary' : 'text.secondary',
        fontStyle: text ? 'normal' : 'italic',
        transform: focused ? 'scale(1.3)' : 'scale(1)',
        animation: focused ? `${pulse} 0.3s ease-out forwards` : 'none',
        transformOrigin: 'center',
      }}
    >
      {text || defaultText}
    </Typography>
  </Box>
);

interface InputFieldProps {
  label: string;
}

export const ShopfloorInputField = ({ label }: InputFieldProps): React.JSX.Element => (
  <TextField
    size="small"
    label={label}
    variant="outlined"
    fullWidth
    disabled
    sx={{
      pointerEvents: 'none',
      '& .MuiInputBase-root': { height: 28, fontSize: 9 },
      '& .MuiInputLabel-root': { fontSize: 9 },
    }}
  />
);

/** Results dialog overlay for showing finder results - covers ~80% of screen from bottom */
interface ResultsDialogProps {
  /** Duration in seconds shown in the preview */
  duration?: number | '';
  /** Whether the corresponding input field is focused */
  focused?: boolean;
}

export const ShopfloorResultsDialog = ({ duration, focused = false }: ResultsDialogProps): React.JSX.Element => {
  const hasValidDuration = duration !== undefined && duration !== '' && duration > 0;

  return (
    <Box
      sx={{
        position: 'absolute',
        top: '20%',
        bottom: 0,
        left: 0,
        right: 0,
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Dialog AppBar header */}
      <Box sx={{ bgcolor: '#434343', px: 1, py: 0.5, display: 'flex', alignItems: 'center' }}>
        <Typography sx={{ fontSize: 8, color: '#fff', mr: 0.5 }}>✕</Typography>
        <Typography sx={{ fontSize: 9, fontWeight: 600, color: '#fff' }}>Finder Matches</Typography>
      </Box>

      {/* Dialog content */}
      <Box sx={{ p: 1, flexGrow: 1, overflow: 'hidden' }}>
        {/* Result card example */}
        <Card variant="outlined" sx={{ mb: 0.5 }}>
          <CardContent sx={{ p: 0.5, '&:last-child': { pb: 0.5 } }}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <AccountTree sx={{ fontSize: 10, color: 'text.secondary' }} />
              <Typography sx={{ fontSize: 7, color: 'text.secondary', flexGrow: 1 }}>shelf-1</Typography>
              <LinkSharp sx={{ fontSize: 10, color: '#5fd362' }} />
              <Typography sx={{ fontSize: 8, fontWeight: 600, color: '#5fd362' }}>3</Typography>
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mb: 0.5 }}>
          <CardContent sx={{ p: 0.5, '&:last-child': { pb: 0.5 } }}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <AccountTree sx={{ fontSize: 10, color: 'text.secondary' }} />
              <Typography sx={{ fontSize: 7, color: 'text.secondary', flexGrow: 1 }}>shelf-2</Typography>
              <LinkSharp sx={{ fontSize: 10, color: '#5fd362' }} />
              <Typography sx={{ fontSize: 8, fontWeight: 600, color: '#5fd362' }}>1</Typography>
            </Stack>
          </CardContent>
        </Card>

        {/* Duration indicator */}
        <Box sx={{ width: '75%', mx: 'auto' }}>
          <Typography
            sx={{
              fontSize: 9,
              fontWeight: focused ? 700 : 400,
              color: 'text.secondary',
              textAlign: 'center',
              mt: 1,
              transform: focused ? 'scale(1.3)' : 'scale(1)',
              animation: focused ? `${pulse} 0.3s ease-out forwards` : 'none',
              transformOrigin: 'center',
            }}
          >
            {hasValidDuration ? (
              <>
                Auto-closes in{' '}
                <Box
                  component="span"
                  sx={{
                    fontWeight: 700,
                    color: 'text.primary',
                  }}
                >
                  {duration}s
                </Box>
              </>
            ) : (
              <Box component="span" sx={{ fontStyle: 'italic' }}>
                Results won&apos;t show
              </Box>
            )}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
